import React, { useState, useEffect, useRef } from "react";
import {
    Search,
    Upload,
    SlidersHorizontal,
    User,
    UserPlus,
    FileText,
    Check,
    Info,
    Trash2,
    X,
    ChevronDown,
    ArrowUp,
    ArrowDown,
} from "lucide-react";
import { AccountStatus, ContactItem, DateFilter, Filters, VerificationStatus } from "./types";
import useCrmApi from "../../../components/hook/crm-req";
import Pagination from "../../../components/pagination";
import { getAccountStatusColor, getAccountStatusFromCustomer, getDisplayStatus, getStatusCustomerColors, getStatusDotColor } from "../../../utils/function";
import { FaEnvelope, FaWhatsapp } from "react-icons/fa";
import { toast } from "sonner";
import { WalletBalanceData } from "../../marketing-pages/campaign/types";
import { ContactModal } from "../../../components/crm/customer/update-customer";
import useCrmFormData from "../../../components/hook/crm-formdata-req";
import ReuseableDrawer from "../../../components/resuable-drawer";
import CustomerDetail from "./details/customer_details";
import { useBvnStatusVisibility } from "../../../components/hook/use-bvn-status-visibility";

// Define sort configuration type
type SortField = 'customer' | 'dateAdded' | 'walletBalance';
type SortOrder = 'asc' | 'desc';

interface SortConfig {
    field: SortField;
    order: SortOrder;
}

// Convert API ContactItem to Customer format
const mapContactToCustomer = (contact: ContactItem): ContactItem => {
    // Format date for display
    const formatDate = (dateString: string): string => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    // An "onboarded" tag means this customer is verified, full stop —
    // regardless of whatever custom.status otherwise says.
    const isOnboarded = contact.tags?.includes("onboarded");

    return {
        id: contact.id,
        email: contact.email,
        first_name: `${contact.first_name || contact.firstName || ""} ${contact.last_name || contact.lastName || ""}`.trim() || "Unnamed",
        phone: contact.phone || "",
        status: isOnboarded ? "verified" : (contact.custom?.status || contact.status),
        tags: contact.tags,
        custom: contact.custom,
        updated_at: contact.updated_at || contact.updatedAt ?
            formatDate(contact.updated_at || contact.updatedAt || new Date().toISOString()) :
            'N/A'
    };
};

const StatusBadge: React.FC<{ status?: string }> = ({ status }) => {
    const displayText = getDisplayStatus(status);
    const bgColor = getStatusCustomerColors(status);
    const dotColor = getStatusDotColor(status);

    // Check what needs to be verified
    const rawStatus = status?.toLowerCase();
    const isEmailVerified = rawStatus === "email_verified" || rawStatus === "verified";
    const isPhoneVerified = rawStatus === "phone_verified" || rawStatus === "verified";
    const isOtpPending = rawStatus === "otp_pending";

    // Determine which icons to show
    const showEmailIcon = !isEmailVerified && (isOtpPending || rawStatus === "pending" || rawStatus === "phone_verified" || rawStatus === "unknown");
    const showPhoneIcon = !isPhoneVerified && (isOtpPending || rawStatus === "pending" || rawStatus === "email_verified" || rawStatus === "unknown");

    // For email_verified, show just the badge without icons
    if (rawStatus === "email_verified") {
        return (
            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${bgColor}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${dotColor}`} />
                {displayText}
            </span>
        );
    }

    return (
        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${bgColor}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${dotColor}`} />
            {(showPhoneIcon || showEmailIcon) && (
                <span className="flex items-center gap-1">
                    {showPhoneIcon && <FaWhatsapp className="w-3 h-3 text-[#FBBF24]" />}
                    {showEmailIcon && <FaEnvelope className="w-3 h-3 text-[#FBBF24]" />}
                </span>
            )}
            {displayText}
        </span>
    );
};

const ExportIcon: React.FC<{ size?: number; className?: string }> = ({
    size = 16,
    className = "",
}) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
    >
        <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
        <polyline points="16 6 12 2 8 6" />
        <line x1="12" y1="2" x2="12" y2="15" />
    </svg>
);

const DownloadIcon: React.FC<{ size?: number; className?: string }> = ({
    size = 16,
    className = "",
}) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
    >
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
        <polyline points="7 10 12 15 17 10" />
        <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
);

const Checkbox: React.FC<{
    checked: boolean;
    onChange: () => void;
}> = ({ checked, onChange }) => (
    <button
        onClick={(e) => {
            e.stopPropagation();
            onChange();
        }}
        className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all shrink-0 ${checked
            ? "bg-green-500 border-green-500"
            : "border-gray-300 dark:border-gray-600 hover:border-indigo-400 dark:hover:border-indigo-500"
            }`}
    >
        {checked && (
            <svg
                width="11"
                height="9"
                viewBox="0 0 11 9"
                fill="none"
                className="text-white"
            >
                <path
                    d="M1 4L4 7.5L10 1"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
        )}
    </button>
);

const FilterPanel: React.FC<{
    walletBallance: WalletBalanceData | null;
    filters: Filters;
    onClose: () => void;
    onApply: (f: Filters) => void;
    onReset: () => void;
}> = ({ walletBallance, filters, onClose, onApply, onReset }) => {
    const [walletError, setWalletError] = useState("");
    const MAX_WALLET = 1000000;
    const MIN_WALLET = 0;

    const [local, setLocal] = useState<Filters>({
        ...filters,
        minWalletBalance: filters.minWalletBalance ?? MIN_WALLET,
        maxWalletBalance:
            filters.maxWalletBalance && filters.maxWalletBalance > 0
                ? filters.maxWalletBalance
                : MAX_WALLET,
    });

    // 👇 input states as strings so typing feels natural
    const [minInput, setMinInput] = useState(
        String(filters.minWalletBalance ?? MIN_WALLET)
    );
    const [maxInput, setMaxInput] = useState(
        String(
            filters.maxWalletBalance && filters.maxWalletBalance > 0
                ? filters.maxWalletBalance
                : MAX_WALLET
        )
    );

    useEffect(() => {
        const minVal = filters.minWalletBalance ?? MIN_WALLET;
        const maxVal =
            filters.maxWalletBalance && filters.maxWalletBalance > 0
                ? filters.maxWalletBalance
                : MAX_WALLET;

        setLocal({
            ...filters,
            minWalletBalance: minVal,
            maxWalletBalance: maxVal,
        });

        setMinInput(String(minVal));
        setMaxInput(String(maxVal));
        setWalletError("");
    }, [filters]);

    const validateWalletRange = (min: number, max: number) => {
        if (max <= min) {
            setWalletError("Max wallet balance must be higher than min wallet balance.");
            return false;
        }
        setWalletError("");
        return true;
    };

    const handleApply = () => {
        const minVal = Number(minInput || 0);
        const maxVal = Number(maxInput || 0);

        if (!validateWalletRange(minVal, maxVal)) return;

        onApply({
            ...local,
            minWalletBalance: minVal,
            maxWalletBalance: maxVal,
        });
    };

    const updateVerification = (value: VerificationStatus) => {
        setLocal({ ...local, verificationStatus: value });
    };

    const updateAccountStatus = (value: AccountStatus) => {
        setLocal({ ...local, accountStatus: value });
    };

    const updateDate = (value: DateFilter) => {
        setLocal({ ...local, dateAdded: value });
    };

    const updateMinWallet = (value: number) => {
        const safeValue = Math.max(MIN_WALLET, Math.min(value, MAX_WALLET));

        setLocal((prev) => ({
            ...prev,
            minWalletBalance: safeValue,
        }));

        setMinInput(String(safeValue));
        validateWalletRange(safeValue, local.maxWalletBalance ?? MAX_WALLET);
    };

    const updateMaxWallet = (value: number) => {
        const safeValue = Math.max(MIN_WALLET, Math.min(value, MAX_WALLET));

        setLocal((prev) => ({
            ...prev,
            maxWalletBalance: safeValue,
        }));

        setMaxInput(String(safeValue));
        validateWalletRange(local.minWalletBalance ?? MIN_WALLET, safeValue);
    };

    return (
        <div className="fixed z-50 inset-0 flex justify-end" onClick={onClose}>
            <div
                className="relative bg-white dark:bg-gray-900 w-full max-w-xs h-full shadow-2xl flex flex-col p-6 overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-2 font-semibold text-gray-800 dark:text-gray-200">
                        <SlidersHorizontal size={16} className="text-indigo-600" />
                        Filters
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-[#7B7B7B] font-semibold dark:text-gray-500"
                    >
                        <X size={18} />
                    </button>
                </div>

                <div className="space-y-5 flex-1">
                    {/* Account Status Filter */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Account Status
                        </label>
                        <div className="relative">
                            <select
                                value={local.accountStatus}
                                onChange={(e) => updateAccountStatus(e.target.value as AccountStatus)}
                                className="w-full appearance-none border border-gray-200 dark:border-gray-700 rounded-xl px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-300 dark:focus:ring-indigo-500/30 pr-10"
                            >
                                <option value="All">All</option>
                                <option value="active">Active</option>
                                <option value="onboarded">Onboarded</option>
                                <option value="waitlist">Waitlist</option>
                                <option value="blocked">Blocked</option>
                                <option value="unverified">Unverified</option>
                            </select>
                            <ChevronDown
                                size={16}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#7B7B7B] font-semibold dark:text-gray-500 pointer-events-none"
                            />
                        </div>
                    </div>

                    {/* Verification Status Filter */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Verification Status
                        </label>
                        <div className="relative">
                            <select
                                value={local.verificationStatus}
                                onChange={(e) => updateVerification(e.target.value as VerificationStatus)}
                                className="w-full appearance-none border border-gray-200 dark:border-gray-700 rounded-xl px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-300 dark:focus:ring-indigo-500/30 pr-10"
                            >
                                <option value="All">All</option>
                                <option value="Email Verified">Email Verified</option>
                                <option value="Phone Verified">Phone Verified</option>
                                <option value="Unverified">Unverified</option>
                            </select>
                            <ChevronDown
                                size={16}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#7B7B7B] font-semibold dark:text-gray-500 pointer-events-none"
                            />
                        </div>
                    </div>

                    {/* Date Added Filter */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Date Added
                        </label>
                        <div className="relative">
                            <select
                                value={local.dateAdded}
                                onChange={(e) => updateDate(e.target.value as DateFilter)}
                                className="w-full appearance-none border border-gray-200 dark:border-gray-700 rounded-xl px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-300 dark:focus:ring-indigo-500/30 pr-10"
                            >
                                <option>All time</option>
                                <option>Last 7 days</option>
                                <option>Last 30 days</option>
                                <option>Last 90 days</option>
                            </select>
                            <ChevronDown
                                size={16}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#7B7B7B] font-semibold dark:text-gray-500 pointer-events-none"
                            />
                        </div>
                    </div>

                    {/* Wallet Balance Range */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                            Wallet Balance
                        </label>

                        <div className="rounded-2xl p-4 border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800">
                            <div className="flex items-center justify-between mb-6 gap-3">
                                {/* Min Wallet Input */}
                                <div className="flex-1">
                                    <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Min</label>
                                    <div className="flex items-center bg-[#EAEDFB] dark:bg-gray-700 rounded-md px-3 py-3 min-w-[120px]">
                                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300 mr-1">₦</span>
                                        <input
                                            type="number"
                                            min={MIN_WALLET}
                                            max={MAX_WALLET}
                                            step={1}
                                            value={minInput}
                                            onFocus={() => {
                                                if (minInput === "0") setMinInput("");
                                            }}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                setMinInput(value);

                                                const numericValue = Number(value || 0);
                                                setLocal((prev) => ({
                                                    ...prev,
                                                    minWalletBalance: numericValue,
                                                }));

                                                validateWalletRange(
                                                    numericValue,
                                                    Number(maxInput || 0)
                                                );
                                            }}
                                            onBlur={() => {
                                                const finalValue = Math.max(
                                                    MIN_WALLET,
                                                    Math.min(Number(minInput || 0), MAX_WALLET)
                                                );
                                                setMinInput(String(finalValue));
                                                setLocal((prev) => ({
                                                    ...prev,
                                                    minWalletBalance: finalValue,
                                                }));
                                                validateWalletRange(
                                                    finalValue,
                                                    Number(maxInput || 0)
                                                );
                                            }}
                                            className="w-full bg-transparent outline-none text-sm font-medium text-gray-700 dark:text-gray-300 placeholder:text-gray-400 dark:placeholder:text-gray-500 [appearance:textfield]"
                                            placeholder="0"
                                        />
                                    </div>
                                </div>

                                {/* Max Wallet Input */}
                                <div className="flex-1">
                                    <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1 text-right">Max</label>
                                    <div className="flex items-center bg-[#EAEDFB] dark:bg-gray-700 rounded-md px-3 py-3 min-w-[120px]">
                                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300 mr-1">₦</span>
                                        <input
                                            type="number"
                                            min={MIN_WALLET}
                                            max={MAX_WALLET}
                                            step={1}
                                            value={maxInput}
                                            onFocus={() => {
                                                if (maxInput === String(MAX_WALLET)) setMaxInput("");
                                            }}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                setMaxInput(value);

                                                const numericValue = Number(value || 0);
                                                setLocal((prev) => ({
                                                    ...prev,
                                                    maxWalletBalance: numericValue,
                                                }));

                                                validateWalletRange(
                                                    Number(minInput || 0),
                                                    numericValue
                                                );
                                            }}
                                            onBlur={() => {
                                                const finalValue = Math.max(
                                                    MIN_WALLET,
                                                    Math.min(Number(maxInput || 0), MAX_WALLET)
                                                );
                                                setMaxInput(String(finalValue));
                                                setLocal((prev) => ({
                                                    ...prev,
                                                    maxWalletBalance: finalValue,
                                                }));
                                                validateWalletRange(
                                                    Number(minInput || 0),
                                                    finalValue
                                                );
                                            }}
                                            className="w-full bg-transparent outline-none text-sm font-medium text-gray-700 dark:text-gray-300 text-right placeholder:text-gray-400 dark:placeholder:text-gray-500 [appearance:textfield]"
                                            placeholder="1000000"
                                        />
                                    </div>
                                </div>
                            </div>

                            {walletError && (
                                <p className="mt-2 z-30 text-xs text-red-500 font-medium">
                                    {walletError}
                                </p>
                            )}

                            {(() => {
                                const minVal = Number(minInput || 0);
                                const maxVal = Number(maxInput || 0);

                                const minPercent =
                                    ((minVal - MIN_WALLET) / (MAX_WALLET - MIN_WALLET)) * 100;
                                const maxPercent =
                                    ((maxVal - MIN_WALLET) / (MAX_WALLET - MIN_WALLET)) * 100;

                                return (
                                    <div className="relative h-10 flex items-center">
                                        {/* Base Track */}
                                        <div className="absolute w-full h-1 bg-[#D9D9E8] dark:bg-gray-700 rounded-full" />

                                        {/* Active Range */}
                                        <div
                                            className="absolute h-1 bg-indigo-600 rounded-full"
                                            style={{
                                                left: `${Math.min(minPercent, maxPercent)}%`,
                                                width: `${Math.abs(maxPercent - minPercent)}%`,
                                            }}
                                        />

                                        {/* Min Range */}
                                        <input
                                            type="range"
                                            min={MIN_WALLET}
                                            max={MAX_WALLET}
                                            step={1000}
                                            value={minVal}
                                            onChange={(e) => updateMinWallet(Number(e.target.value))}
                                            className="range-thumb absolute w-full appearance-none bg-transparent"
                                        />

                                        {/* Max Range */}
                                        <input
                                            type="range"
                                            min={MIN_WALLET}
                                            max={MAX_WALLET}
                                            step={1000}
                                            value={maxVal}
                                            onChange={(e) => updateMaxWallet(Number(e.target.value))}
                                            className="range-thumb absolute w-full appearance-none bg-transparent"
                                        />
                                    </div>
                                );
                            })()}
                        </div>
                    </div>
                </div>

                <div className="mt-8 space-y-3">
                    <button
                        onClick={handleApply}
                        disabled={!!walletError}
                        className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-300 disabled:cursor-not-allowed text-white font-semibold py-3 rounded-xl transition-colors"
                    >
                        Apply Filters
                    </button>
                    <button
                        onClick={() => {
                            onReset();
                        }}
                        className="w-full border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 font-semibold py-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                    >
                        Reset Filters
                    </button>
                </div>
            </div>
        </div>
    );
};


// Sortable Header Component
const SortableHeader: React.FC<{
    field: SortField;
    currentSort: SortConfig | null;
    onSort: (field: SortField) => void;
    children: React.ReactNode;
    className?: string;
}> = ({ field, currentSort, onSort, children, className = "" }) => {
    const isActive = currentSort?.field === field;
    const sortOrder = isActive ? currentSort.order : null;

    return (
        <th className={className}>
            <button
                onClick={() => onSort(field)}
                className="flex items-center gap-1 transition-colors group"
            >
                <span className="w-4 h-4 flex items-center justify-center">
                    {!isActive && (
                        <ArrowUp size={14} className="text-[#7B7B7B] font-semibold dark:text-gray-500" />
                    )}
                    {isActive && sortOrder === 'asc' && (
                        <ArrowUp size={14} className="text-indigo-600" />
                    )}
                    {isActive && sortOrder === 'desc' && (
                        <ArrowDown size={14} className="text-indigo-600" />
                    )}
                </span>
                {children}
            </button>
        </th>
    );
};

const CustomerRow: React.FC<{
    customer: ContactItem;
    selected: boolean;
    showCheckbox: boolean;
    showBvnStatus: boolean;
    onToggle: () => void;
    onClick: () => void;
    onDelete: (e: React.MouseEvent) => void;
}> = ({ customer, selected, showCheckbox, showBvnStatus, onToggle, onClick, onDelete }) => {
    const accountStatus = getAccountStatusFromCustomer(customer);

    return (
        <tr
            className="border-b border-gray-50 dark:border-gray-800 hover:bg-indigo-50/30 dark:hover:bg-indigo-500/10 transition-colors cursor-pointer group"
            onClick={onClick}
        >
            {showCheckbox && (
                <td className="pl-4 pr-2 py-4">
                    <Checkbox checked={selected} onChange={onToggle} />
                </td>
            )}
            <td className="px-4 py-4">
                <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-indigo-100 dark:bg-indigo-500/20 flex items-center justify-center shrink-0">
                        <User size={17} className="text-indigo-500 dark:text-indigo-400" />
                    </div>
                    <div>
                        <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">{customer?.first_name}</p>
                        <p className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500">{customer.phone}</p>
                    </div>
                </div>
            </td>
            <td className="px-4 py-4 text-sm text-gray-600 dark:text-gray-400 max-w-[200px] truncate" title={customer?.email}>
                {customer?.email}
            </td>
            <td className="px-4 py-4">
                <p className="text-sm font-medium text-gray-800 dark:text-gray-200">{customer?.updated_at}</p>
            </td>
            <td className="px-4 py-4">
                <span
                    className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-medium capitalize ${getAccountStatusColor(accountStatus)}`}
                >
                    {accountStatus}
                </span>
            </td>
            <td className="px-4 py-4">
                <StatusBadge status={customer?.status} />
            </td>
            {showBvnStatus && (
                <td className="px-4 py-4">
                    <StatusBadge status={customer?.custom?.bvn_hash ? "Verified" : "Unverified"} />
                </td>
            )}
            <td className="px-4 py-4">
                <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                    {customer?.custom?.wallet_balance !== undefined ?
                        `₦${customer.custom.wallet_balance.toLocaleString()}.00` :
                        '₦0'}
                </p>
            </td>
            <td className="px-4 py-4">
                <div className="flex items-center gap-2 justify-end">
                    <button
                        // onClick={(e) => e.stopPropagation()}
                        className="p-2 rounded-lg text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-500/15 transition-colors"
                    >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M11 18C11 18.5523 11.4477 19 12 19C12.5523 19 13 18.5523 13 18C13 17.4477 12.5523 17 12 17C11.4477 17 11 17.4477 11 18Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                            <path d="M11 12C11 12.5523 11.4477 13 12 13C12.5523 13 13 12.5523 13 12C13 11.4477 12.5523 11 12 11C11.4477 11 11 11.4477 11 12Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                            <path d="M11 6C11 6.55228 11.4477 7 12 7C12.5523 7 13 6.55228 13 6C13 5.44772 12.5523 5 12 5C11.4477 5 11 5.44772 11 6Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                        </svg>


                    </button>
                </div>
            </td>
        </tr>
    );
};

const CustomerCard: React.FC<{
    customer: ContactItem;
    selected: boolean;
    showCheckbox: boolean;
    onToggle: () => void;
    onClick: () => void;
    onDelete: (e: React.MouseEvent) => void;
}> = ({ customer, selected, showCheckbox, onToggle, onClick, onDelete }) => {
    const accountStatus = getAccountStatusFromCustomer(customer);

    return (
        <div
            className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm p-4 cursor-pointer hover:border-indigo-200 dark:hover:border-indigo-500/50 transition-all active:scale-[0.99]"
            onClick={onClick}
        >
            <div className="flex items-start gap-3">
                {showCheckbox && (
                    <Checkbox checked={selected} onChange={onToggle} />
                )}
                <div className="w-10 h-10 rounded-full bg-indigo-100 dark:bg-indigo-500/20 flex items-center justify-center shrink-0">
                    <User size={18} className="text-indigo-500 dark:text-indigo-400" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-semibold text-gray-800 dark:text-gray-200 truncate">
                            {customer?.first_name}
                        </p>
                        <StatusBadge status={customer?.status || customer?.custom?.status} />
                    </div>
                    <p className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 mt-0.5">{customer.phone}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate" title={customer?.email}>{customer?.email}</p>
                    <p className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 mt-1">{customer?.updated_at}</p>
                    <div className="flex items-center justify-between mt-1">
                        <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium capitalize ${getAccountStatusColor(accountStatus)}`}>
                            {accountStatus}
                        </span>
                        <p className="text-xs font-semibold text-green-600">
                            {customer?.custom?.wallet_balance !== undefined ?
                                `₦${customer.custom.wallet_balance.toLocaleString()}` :
                                '₦0'}
                        </p>
                    </div>
                </div>
            </div>
            <div className="flex items-center gap-2 justify-end mt-3 pt-3 border-t border-gray-50 dark:border-gray-800">
                <button
                    onClick={(e) => e.stopPropagation()}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-indigo-600 dark:text-indigo-400 text-xs font-medium hover:bg-indigo-50 dark:hover:bg-indigo-500/15 transition-colors"
                >
                    <ExportIcon size={13} /> Export
                </button>
                <button
                    onClick={onDelete}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-red-500 dark:text-red-400 text-xs font-medium hover:bg-red-50 dark:hover:bg-red-500/15 transition-colors"
                >
                    <Trash2 size={13} /> Delete
                </button>
            </div>
        </div>
    );
};

const FilterChip: React.FC<{ label: string; onRemove: () => void }> = ({
    label,
    onRemove,
}) => (
    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-indigo-50 dark:bg-indigo-500/15 text-indigo-700 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-500/40">
        {label}
        <button onClick={onRemove} className="hover:text-indigo-900 dark:hover:text-indigo-300 transition-colors">
            <X size={12} />
        </button>
    </span>
);

interface CsvUploadModalProps {
    isOpen: boolean;
    onClose: () => void;
    onComplete: () => void;
}

const CsvUploadModal: React.FC<CsvUploadModalProps> = ({ isOpen, onClose, onComplete }) => {
    const token = localStorage.getItem("authToken");
    const inputRef = useRef<HTMLInputElement>(null);
    const [file, setFile] = useState<File | null>(null);
    const [progress, setProgress] = useState(0);
    const [completed, setCompleted] = useState(false);
    const [validatedCount, setValidatedCount] = useState<number | null>(null);

    const { makeRequest: uploadContacts } = useCrmFormData(`/contacts/upload-csv`, "POST", {
        Authorization: `Bearer ${token}`,
    });

    useEffect(() => {
        if (!isOpen) {
            setFile(null);
            setProgress(0);
            setCompleted(false);
            setValidatedCount(null);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const handleFileUpload = async (selectedFile: File) => {
        if (!selectedFile.name.toLowerCase().endsWith(".csv")) {
            toast.error("Please upload a CSV file.");
            return;
        }

        setFile(selectedFile);
        setProgress(15);

        const progressTimer = window.setInterval(() => {
            setProgress((current) => (current >= 70 ? current : current + 10));
        }, 250);

        try {
            const formData = new FormData();
            formData.append("file", selectedFile);

            const [response, status] = await uploadContacts(formData);
            window.clearInterval(progressTimer);

            if (status >= 200 && status < 300) {
                setProgress(100);
                setCompleted(true);
                setValidatedCount(
                    Number(
                        response?.validated_contacts ??
                        response?.valid_contacts ??
                        response?.total ??
                        response?.count
                    ) || null
                );
                toast.success(response?.message || "Contacts uploaded successfully.");
            } else {
                setProgress(0);
                setFile(null);
                toast.error(response?.detail || response?.message || "Failed to upload contacts.");
            }
        } catch {
            window.clearInterval(progressTimer);
            setProgress(0);
            setFile(null);
            toast.error("Failed to upload contacts.");
        }
    };

    const closeAndRefresh = () => {
        onComplete();
        onClose();
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/35 p-4"
            onClick={(e) => e.target === e.currentTarget && onClose()}
        >
            <div className="relative w-full max-w-xl rounded-2xl bg-white dark:bg-gray-900 p-8 shadow-2xl">
                <button
                    onClick={onClose}
                    className="absolute right-5 top-5 rounded-lg p-1 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
                >
                    <X size={18} />
                </button>

                <div className="mx-auto mb-7 flex h-16 w-16 items-center justify-center rounded-xl bg-indigo-100 dark:bg-indigo-500/20 text-indigo-600 dark:text-indigo-400">
                    <Upload size={24} />
                </div>

                {!completed ? (
                    <>
                        <div className="text-center">
                            <h2 className="text-xl font-bold text-[#3A3428] dark:text-gray-100">
                                {file ? "Uploading Customers List" : "Upload  Customers"}
                            </h2>
                            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                                {file ? file.name : "Choose a CSV file to import customers."}
                            </p>
                        </div>

                        {!file ? (
                            <div className="mt-8 rounded-2xl border-2 border-dashed border-gray-200 dark:border-gray-700 p-8 text-center">
                                <input
                                    ref={inputRef}
                                    type="file"
                                    accept=".csv,text/csv"
                                    className="hidden"
                                    onChange={(e) => {
                                        const selectedFile = e.target.files?.[0];
                                        if (selectedFile) handleFileUpload(selectedFile);
                                    }}
                                />
                                <button
                                    onClick={() => inputRef.current?.click()}
                                    className="mx-auto flex items-center gap-2 rounded-xl bg-indigo-600 px-5 py-3 text-sm font-semibold text-white hover:bg-indigo-700"
                                >
                                    <FileText size={16} />
                                    Select CSV File
                                </button>
                                <p className="mt-3 text-xs text-[#7B7B7B] font-semibold dark:text-gray-500">CSV only</p>
                            </div>
                        ) : (
                            <div className="mt-8">
                                <div className="mb-3 flex items-center justify-between text-sm">
                                    <span className="font-medium text-indigo-600">{progress}% Uploaded</span>
                                    <span className="text-xs text-gray-700 dark:text-gray-300">
                                        {(file.size / (1024 * 1024)).toFixed(1)}MB
                                    </span>
                                </div>
                                <div className="h-3 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                                    <div
                                        className="h-full rounded-full bg-indigo-600 transition-all"
                                        style={{ width: `${progress}%` }}
                                    />
                                </div>
                                <div className="mt-4 flex gap-3 rounded-lg bg-indigo-50 dark:bg-indigo-500/10 p-4 text-xs text-slate-700 dark:text-slate-300">
                                    <Info size={18} className="mt-0.5 flex-shrink-0 text-indigo-600" />
                                    <p>We are processing your contacts and validating formats. This may take a few more seconds. Do not refresh the page.</p>
                                </div>
                                <button
                                    onClick={onClose}
                                    className="mt-7 w-full rounded-xl border border-gray-200 dark:border-gray-700 py-3 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                                >
                                    Cancel Upload
                                </button>
                            </div>
                        )}
                    </>
                ) : (
                    <>
                        <div className="text-center">
                            <h2 className="text-xl font-bold text-[#3A3428] dark:text-gray-100">Upload Complete</h2>
                            <p className="mt-2 text-sm text-[#3A3428] dark:text-gray-300">
                                Your file processing is complete.
                            </p>
                            <p className="mt-3 text-sm text-[#3A3428] dark:text-gray-300">
                                <span className="font-bold">{validatedCount?.toLocaleString() || "Your"} contacts</span> are validated
                                <br />
                                and ready for import into your broadcast list.
                            </p>
                        </div>

                        <div className="mt-8 flex items-center gap-4 rounded-lg bg-gray-100 dark:bg-gray-800 p-4">
                            <div className="flex h-10 w-10 items-center justify-center rounded bg-white dark:bg-gray-900 text-indigo-600 dark:text-indigo-400">
                                <FileText size={20} />
                            </div>
                            <div className="min-w-0 flex-1">
                                <p className="text-xs text-gray-500 dark:text-gray-400">File Source</p>
                                <p className="truncate text-sm font-medium text-indigo-600 dark:text-indigo-400 underline">{file?.name}</p>
                            </div>
                            <span className="flex h-7 w-7 items-center justify-center rounded-full border border-green-700 dark:border-green-500/50 bg-green-200 dark:bg-green-500/20 text-green-700 dark:text-green-400">
                                <Check size={16} />
                            </span>
                        </div>

                        <button
                            onClick={closeAndRefresh}
                            className="mt-8 w-full rounded-lg bg-indigo-600 py-3 text-sm font-semibold text-white hover:bg-indigo-700"
                        >
                            Confirm Import
                        </button>
                    </>
                )}
            </div>
        </div>
    );
};

// ── Main Page ─────────────────────────────────────────────────────────────────
const MAX_WALLET = 1000000;
const DEFAULT_FILTERS: Filters = {
    verificationStatus: "All",
    accountStatus: "All",
    dateAdded: "All time",
    minWalletBalance: 0,
    maxWalletBalance: MAX_WALLET,
};

const Customers: React.FC = () => {
    const showBvnStatus = useBvnStatusVisibility();
    const [customers, setCustomers] = useState<ContactItem[]>([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [unselectedIds, setUnselectedIds] = useState<Set<string>>(new Set());
    const [isAllSelected, setIsAllSelected] = useState(false);
    const [showFilter, setShowFilter] = useState(false);
    const [activeFilters, setActiveFilters] = useState<Filters>(DEFAULT_FILTERS);
    const [openUpdateModal, setOpenUpdateModal] = useState(false);
    const [showUploadMenu, setShowUploadMenu] = useState(false);
    const [showCsvUploadModal, setShowCsvUploadModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);
    const params = new URLSearchParams(new URL(window.location.href).search);
    const [currentPage, setCurrentPage] = useState(Number(params.get("page") || 1));
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [pageSize, setPageSize] = useState(Number(params.get("per_page")) || 10);
    const [walletBallance] = useState<WalletBalanceData | null>(null);
    const uploadMenuRef = useRef<HTMLDivElement>(null);
    const token = localStorage.getItem("authToken");
    const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);

    const { makeRequest: getCustomers } = useCrmApi(`/contacts`, "GET", {
        Authorization: `Bearer ${token}`,
    });

    const { makeRequest: getExported } = useCrmApi(`/contacts/export`, "GET", {
        Authorization: `Bearer ${token}`,
    });


    function updateUrlParams(params: { [key: string]: string | number | null }) {
        const url = new URL(window.location.href);
        Object.keys(params).forEach((key) => {
            if (params[key]) url.searchParams.set(key, params[key]!.toString());
            else url.searchParams.delete(key);
        });
        window.history.pushState({}, "", url.toString());
    }


    // Debounce search term
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
            setCurrentPage(1);
        }, 500);
        return () => clearTimeout(handler);
    }, [searchTerm]);

    // Update URL and fetch data when filters change
    useEffect(() => {
        updateUrlParams({
            page: currentPage !== 1 ? currentPage : null,
            per_page: pageSize !== 10 ? pageSize : null,
            search: debouncedSearchTerm ? debouncedSearchTerm : null,
            status: activeFilters.verificationStatus !== "All" ? activeFilters.verificationStatus : null,
            acc_status: activeFilters.accountStatus !== "All" ? activeFilters.accountStatus : null,
            dateAdded: activeFilters.dateAdded !== "All time" ? activeFilters.dateAdded : null,
            sort_order: sortConfig ? sortConfig.order : null,
            wallet_balance_min: activeFilters.minWalletBalance && activeFilters.minWalletBalance > 0 ? activeFilters.minWalletBalance : null,
            wallet_balance_max: activeFilters.maxWalletBalance && activeFilters.maxWalletBalance < MAX_WALLET ? activeFilters.maxWalletBalance : null,
        });
        fetchData();
    }, [currentPage, pageSize, debouncedSearchTerm, activeFilters, sortConfig]);

    useEffect(() => {
        setIsAllSelected(false);
        setSelectedIds(new Set());
        setUnselectedIds(new Set());
    }, [debouncedSearchTerm, activeFilters, sortConfig]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (uploadMenuRef.current && !uploadMenuRef.current.contains(event.target as Node)) {
                setShowUploadMenu(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const fetchData = async () => {
        setLoading(true);

        const queryParams: Record<string, any> = {
            page: currentPage,
            per_page: pageSize,
        };

        if (debouncedSearchTerm.trim()) {
            queryParams.search = debouncedSearchTerm.trim();
        }

        if (sortConfig) {
            const sortFieldMap = {
                customer: "name",
                dateAdded: "updated_at",
                walletBalance: "wallet_balance",
            };

            queryParams.sort_by = sortFieldMap[sortConfig.field];
            queryParams.sort_order = sortConfig.order;
        }

        if (activeFilters.verificationStatus !== "All") {
            const statusMap: Record<string, string> = {
                "Email Verified": "email_verified",
                "Phone Verified": "phone_verified",
                Unverified: "unverified",
            };

            queryParams.status =
                statusMap[activeFilters.verificationStatus] ||
                activeFilters.verificationStatus.toLowerCase();
        }

        if (activeFilters.accountStatus !== "All") {
            queryParams.acc_status = activeFilters.accountStatus;
        }

        if (
            activeFilters.minWalletBalance &&
            activeFilters.minWalletBalance > 0
        ) {
            queryParams.wallet_balance_min =
                activeFilters.minWalletBalance;
        }

        if (
            activeFilters.maxWalletBalance &&
            activeFilters.maxWalletBalance < MAX_WALLET
        ) {
            queryParams.wallet_balance_max =
                activeFilters.maxWalletBalance;
        }

        if (activeFilters.dateAdded !== "All time") {
            const daysMap = {
                "Last 7 days": 7,
                "Last 30 days": 30,
                "Last 90 days": 90,
            };

            const days = daysMap[activeFilters.dateAdded];
            const cutoffDate = new Date();
            cutoffDate.setDate(cutoffDate.getDate() - days);

            queryParams.from_date = cutoffDate
                .toISOString()
                .split("T")[0];
        }

        try {
            const [response] = await getCustomers(undefined, queryParams);

            if (response?.results && Array.isArray(response.results)) {
                let mappedCustomers = response.results.map(mapContactToCustomer);

                // Frontend protection against bad API data
                if (activeFilters.accountStatus !== "All") {
                    mappedCustomers = mappedCustomers.filter(
                        (customer: ContactItem) =>
                            getAccountStatusFromCustomer(customer) ===
                            activeFilters.accountStatus
                    );
                }

                setCustomers(mappedCustomers);
                setTotalRecords(response.total || 0);
                setTotalPages(
                    Math.ceil((response.total || 0) / pageSize)
                );
            } else {
                setCustomers([]);
                setTotalRecords(0);
                setTotalPages(1);
            }
        } catch (error) {
            setCustomers([]);
        } finally {
            setLoading(false);
        }
    };

    // Handle sorting
    const handleSort = (field: SortField) => {
        setSortConfig((prevConfig) => {
            if (prevConfig?.field === field) {
                return {
                    field,
                    order: prevConfig.order === 'asc' ? 'desc' : 'asc',
                };
            } else {
                return {
                    field,
                    order: 'asc',
                };
            }
        });
        setCurrentPage(1);
    };

    const buildExportQueryParams = () => {
        const queryParams: Record<string, any> = {
            scope: "filtered",
            upload_to_s3: 0,
        };

        if (debouncedSearchTerm.trim()) {
            queryParams.search = debouncedSearchTerm.trim();
        }

        // Add sort parameter for export
        if (sortConfig) {
            let sortField = "";
            switch (sortConfig.field) {
                case 'customer':
                    sortField = "name";
                    break;
                case 'dateAdded':
                    sortField = "updated_at";
                    break;
                case 'walletBalance':
                    sortField = "wallet_balance";
                    break;
            }
            queryParams.sort_by = sortField;
            queryParams.sort_order = sortConfig.order;
        }

        if (activeFilters.verificationStatus !== "All") {
            let apiStatus: string;
            switch (activeFilters.verificationStatus) {
                case "Email Verified":
                    apiStatus = "email_verified";
                    break;
                case "Phone Verified":
                    apiStatus = "phone_verified";
                    break;
                case "Unverified":
                    apiStatus = "unverified";
                    break;
                default:
                    apiStatus = activeFilters.verificationStatus.toLowerCase();
            }
            queryParams.status = apiStatus;
        }

        if (activeFilters.accountStatus !== "All") {
            queryParams.acc_status = activeFilters.accountStatus;
        }

        // Add wallet balance filters for export
        if (activeFilters.minWalletBalance && activeFilters.minWalletBalance > 0) {
            queryParams.wallet_balance_min = activeFilters.minWalletBalance;
        }

        if (activeFilters.maxWalletBalance && activeFilters.maxWalletBalance < MAX_WALLET) {
            queryParams.wallet_balance_max = activeFilters.maxWalletBalance;
        }

        if (activeFilters.dateAdded !== "All time") {
            const daysMap: Partial<Record<DateFilter, number>> = {
                "Last 7 days": 7,
                "Last 30 days": 30,
                "Last 90 days": 90,
            };
            const days = daysMap[activeFilters.dateAdded];
            if (days) {
                const cutoffDate = new Date();
                cutoffDate.setDate(cutoffDate.getDate() - days);
                queryParams.from_date = cutoffDate.toISOString().split("T")[0];
            }
        }

        return queryParams;
    };

    const handleExport = async () => {
        setExporting(true);

        try {
            const queryParams = buildExportQueryParams();

            const [response] = await getExported(undefined, queryParams);

            let csvContent = "";

            if (typeof response === "string") {
                csvContent = response;
            } else if (response?.data && typeof response.data === "string") {
                csvContent = response.data;
            } else if (response?.results && typeof response.results === "string") {
                csvContent = response.results;
            }

            if (!csvContent || csvContent.trim().length < 50) {
                toast.error("Export failed: Received empty or invalid CSV data.");
                return;
            }

            const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `customers_export_${new Date().toISOString().split("T")[0]}.csv`;

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

            toast.success("Export downloaded successfully!");
        } catch (error: any) {

            toast.error("Failed to download export.");
        } finally {
            setExporting(false);
        }
    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    // const handleCustomerClick = (customerId: string) => {
    //     navigate(`/customers/${customerId}`);
    // };

    const handleCustomerClick = (customerId: string) => {
        setSelectedCustomerId(customerId);
    };

    const allSelected = isAllSelected;
    const isCustomerSelected = (id: string) => {
        if (isAllSelected) {
            return !unselectedIds.has(id);
        }
        return selectedIds.has(id);
    };

    const toggleSelect = (id: string) => {
        if (isAllSelected) {
            setUnselectedIds((prev) => {
                const next = new Set(prev);
                if (next.has(id)) {
                    next.delete(id);
                } else {
                    next.add(id);
                }
                return next;
            });
        } else {
            setSelectedIds((prev) => {
                const next = new Set(prev);
                if (next.has(id)) {
                    next.delete(id);
                } else {
                    next.add(id);
                }
                return next;
            });
        }
    };

    const toggleSelectAll = () => {
        if (isAllSelected) {
            setIsAllSelected(false);
            setSelectedIds(new Set());
            setUnselectedIds(new Set());
        } else {
            setIsAllSelected(true);
            setSelectedIds(new Set());
            setUnselectedIds(new Set());
        }
    };

    const selectedCount = isAllSelected
        ? totalRecords - unselectedIds.size
        : selectedIds.size;

    const handleDelete = async (e: React.MouseEvent, id: string) => {
        e.stopPropagation();

        setCustomers((prev) => prev.filter((c) => c.id !== id));

        setSelectedIds((prev) => {
            const next = new Set(prev);
            next.delete(id);
            return next;
        });

        setUnselectedIds((prev) => {
            const next = new Set(prev);
            next.delete(id);
            return next;
        });
    };

    const hasActiveFilters =
        activeFilters.verificationStatus !== "All" ||
        activeFilters.accountStatus !== "All" ||
        activeFilters.dateAdded !== "All time" ||
        (activeFilters.minWalletBalance && activeFilters.minWalletBalance > 0) ||
        (activeFilters.maxWalletBalance && activeFilters.maxWalletBalance < MAX_WALLET);

    const activeFilterCount =
        (activeFilters.verificationStatus !== "All" ? 1 : 0) +
        (activeFilters.accountStatus !== "All" ? 1 : 0) +
        (activeFilters.dateAdded !== "All time" ? 1 : 0) +
        ((activeFilters.minWalletBalance && activeFilters.minWalletBalance > 0) || (activeFilters.maxWalletBalance && activeFilters.maxWalletBalance < MAX_WALLET) ? 1 : 0);

    const handleApplyFilters = (filters: Filters) => {
        setActiveFilters(filters);
        setCurrentPage(1);
        setShowFilter(false);
    };

    const handleResetFilters = () => {
        setActiveFilters(DEFAULT_FILTERS);
        setCurrentPage(1);
        setShowFilter(false);
    };

    if (loading && customers.length === 0) {
        return (
            <div className="min-h-screen bg-white dark:bg-gray-950 rounded-md flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
                    <p className="text-gray-500 dark:text-gray-400">Loading customers...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-white dark:bg-gray-950 rounded-md">
            <div className="mx-auto px-4 sm:px-6 py-6">
                <div className="flex justify-between items-center mb-5">
                    <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">All Customers</h1>
                </div>

                <div className="flex flex-wrap items-center gap-3 mb-4">
                    {selectedCount === 0 ? (
                        <div className="relative" ref={uploadMenuRef}>
                            <button
                                onClick={() => setShowUploadMenu((open) => !open)}
                                className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold px-4 py-2.5 rounded-xl transition-colors shadow-sm"
                            >
                                <Upload size={15} />
                                Upload New
                                <ChevronDown size={14} />
                            </button>

                            {showUploadMenu && (
                                <div className="absolute left-0 top-full z-30 mt-2 w-56 overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-xl">
                                    <button
                                        onClick={() => {
                                            setOpenUpdateModal(true);
                                            setShowUploadMenu(false);
                                        }}
                                        className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-indigo-50 dark:hover:bg-indigo-500/15 hover:text-indigo-700 dark:hover:text-indigo-400"
                                    >
                                        <UserPlus size={16} />
                                        Create Customer
                                    </button>
                                    <button
                                        onClick={() => {
                                            setShowCsvUploadModal(true);
                                            setShowUploadMenu(false);
                                        }}
                                        className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-indigo-50 dark:hover:bg-indigo-500/15 hover:text-indigo-700 dark:hover:text-indigo-400"
                                    >
                                        <FileText size={16} />
                                        Upload Customers
                                    </button>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => {
                                    setIsAllSelected(false);
                                    setSelectedIds(new Set());
                                    setUnselectedIds(new Set());
                                }}
                                className="flex items-center gap-1.5 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 text-sm font-medium px-4 py-2.5 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                            >
                                <X size={14} />
                                Clear All
                            </button>
                            <button
                                onClick={handleExport}
                                disabled={exporting}
                                className="flex items-center gap-1.5 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 text-sm font-medium px-4 py-2.5 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {exporting ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-700 dark:border-gray-300"></div>
                                        Exporting...
                                    </>
                                ) : (
                                    <>
                                        <DownloadIcon size={14} />
                                        Export Selection ({selectedCount})
                                    </>
                                )}
                            </button>
                        </div>
                    )}

                    <div className="flex-1 min-w-[180px] relative">
                        <Search
                            size={15}
                            className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#7B7B7B] font-semibold dark:text-gray-500"
                        />
                        <input
                            type="text"
                            placeholder="Search Customers"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-1/2 pl-10 pr-4 py-2.5 border border-gray-200 dark:border-gray-700 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 dark:focus:ring-indigo-500/30 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                        />
                    </div>

                    <button
                        onClick={toggleSelectAll}
                        className="border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 text-sm font-medium px-4 py-2.5 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors bg-white dark:bg-gray-900 hidden sm:block"
                    >
                        {allSelected ? "Deselect All" : "Select All"}
                    </button>

                    <button
                        onClick={() => setShowFilter(true)}
                        className={`flex items-center gap-2 border text-sm font-medium px-4 py-2.5 rounded-xl transition-colors ${hasActiveFilters
                            ? "bg-indigo-600 border-indigo-600 text-white"
                            : "border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 bg-white dark:bg-gray-900"
                            }`}
                    >
                        <SlidersHorizontal size={15} />
                        Filters
                        {activeFilterCount > 0 && (
                            <span
                                className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${hasActiveFilters
                                    ? "bg-white text-indigo-600"
                                    : "bg-indigo-600 text-white"
                                    }`}
                            >
                                {activeFilterCount}
                            </span>
                        )}
                    </button>
                </div>

                {hasActiveFilters && (
                    <div className="flex flex-wrap gap-2 mb-4">
                        {activeFilters.verificationStatus !== "All" && (
                            <FilterChip
                                label={`Verification: ${activeFilters.verificationStatus}`}
                                onRemove={() => setActiveFilters({ ...activeFilters, verificationStatus: "All" })}
                            />
                        )}
                        {activeFilters.accountStatus !== "All" && (
                            <FilterChip
                                label={`Account: ${activeFilters.accountStatus}`}
                                onRemove={() => setActiveFilters({ ...activeFilters, accountStatus: "All" })}
                            />
                        )}
                        {activeFilters.dateAdded !== "All time" && (
                            <FilterChip
                                label={`Date: ${activeFilters.dateAdded}`}
                                onRemove={() => setActiveFilters({ ...activeFilters, dateAdded: "All time" })}
                            />
                        )}
                        {((activeFilters.minWalletBalance && activeFilters.minWalletBalance > 0) ||
                            (activeFilters.maxWalletBalance && activeFilters.maxWalletBalance < MAX_WALLET)) && (
                                <FilterChip
                                    label={`Wallet: ₦${activeFilters.minWalletBalance?.toLocaleString()} - ₦${activeFilters.maxWalletBalance?.toLocaleString()}`}
                                    onRemove={() => setActiveFilters({ ...activeFilters, minWalletBalance: 0, maxWalletBalance: 1000000 })}
                                />
                            )}
                    </div>
                )}

                <div className="hidden md:block bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm overflow-hidden">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-gray-100 dark:border-gray-800">
                                {(isAllSelected || selectedIds.size > 0) && <th className="w-10 pl-4" />}
                                <SortableHeader
                                    field="customer"
                                    currentSort={sortConfig}
                                    onSort={handleSort}
                                    className="text-left px-4 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 tracking-wide"
                                >
                                    Customer
                                </SortableHeader>
                                <th className="text-left px-4 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 tracking-wide">
                                    Email Address
                                </th>
                                <SortableHeader
                                    field="dateAdded"
                                    currentSort={sortConfig}
                                    onSort={handleSort}
                                    className="text-left px-4 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 tracking-wide"
                                >
                                    Date Added
                                </SortableHeader>
                                <th className="text-left px-4 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 tracking-wide">
                                    Account Status
                                </th>
                                <th className="text-left px-4 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 tracking-wide">
                                    Verification Status
                                </th>
                                {showBvnStatus && (
                                    <th className="text-left px-4 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 tracking-wide">
                                        BVN Status
                                    </th>
                                )}
                                <SortableHeader
                                    field="walletBalance"
                                    currentSort={sortConfig}
                                    onSort={handleSort}
                                    className="text-left px-4 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 tracking-wide"
                                >
                                    Wallet Balance
                                </SortableHeader>
                                <th className="text-right px-4 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 tracking-wide">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {customers.length === 0 ? (
                                <tr>
                                    <td
                                        colSpan={showBvnStatus ? 9 : 8}
                                        className="px-4 py-12 text-center text-[#7B7B7B] font-semibold dark:text-gray-500 text-sm"
                                    >
                                        No customers found.
                                    </td>
                                </tr>
                            ) : (
                                customers.map((c) => (
                                    <CustomerRow
                                        key={c.id}
                                        customer={c}
                                        selected={isCustomerSelected(c.id)}
                                        showCheckbox={isAllSelected || selectedIds.size > 0}
                                        showBvnStatus={showBvnStatus}
                                        onToggle={() => toggleSelect(c.id)}
                                        onClick={() => handleCustomerClick(c.id)}
                                        onDelete={(e) => handleDelete(e, c.id)}
                                    />
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="md:hidden space-y-3">
                    {customers.length === 0 ? (
                        <p className="text-center text-[#7B7B7B] font-semibold dark:text-gray-500 text-sm py-12">
                            No customers found.
                        </p>
                    ) : (
                        customers.map((c) => (
                            <CustomerCard
                                key={c.id}
                                customer={c}
                                selected={isCustomerSelected(c.id)}
                                showCheckbox={selectedIds.size > 0}
                                onToggle={() => toggleSelect(c.id)}
                                onClick={() => handleCustomerClick(c.id)}
                                onDelete={(e) => handleDelete(e, c.id)}
                            />
                        ))
                    )}
                </div>
            </div>

            {showFilter && (
                <FilterPanel
                    filters={activeFilters}
                    onClose={() => setShowFilter(false)}
                    onApply={handleApplyFilters}
                    onReset={handleResetFilters}
                    walletBallance={walletBallance}
                />
            )}

            <ReuseableDrawer
                isOpen={openUpdateModal}
                onClose={() => setOpenUpdateModal(false)}
                title="Add New Contact"
                initialWidth="30%"
            >
                <ContactModal
                    variant="drawer"
                    isOpen={openUpdateModal}
                    onClose={() => setOpenUpdateModal(false)}
                    onCreated={() => fetchData()}
                />
            </ReuseableDrawer>

            <CsvUploadModal
                isOpen={showCsvUploadModal}
                onClose={() => setShowCsvUploadModal(false)}
                onComplete={() => fetchData()}
            />

            {totalPages > 1 && (
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    pageSize={pageSize}
                    onPageSizeChange={(size) => {
                        setPageSize(size);
                        setCurrentPage(1);
                    }}
                />
            )}

            <ReuseableDrawer
                isOpen={!!selectedCustomerId}
                onClose={() => setSelectedCustomerId(null)}
                title={selectedCustomerId ? "Customer Details" : ""}
                initialWidth="52%"
            >
                {selectedCustomerId && (
                    <CustomerDetail
                        id={selectedCustomerId}
                        onClose={() => setSelectedCustomerId(null)}
                        isDrawer={true}
                    />
                )}
            </ReuseableDrawer>
        </div>
    );
};

export default Customers;
